import { Link } from "react-router";
import { readJson } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import {
  StatCard,
  TestimonialCard,
  TestimonialRow,
} from "../components/TestimonialGrid/TestimonialGrid.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./testimonials.module.css";

const PAGE = {
  title: "Client Testimonials",
  description:
    "Testimonials from DOT project managers, engineers and contractors who used 3D Visualization and Road Safety Audit 3D.",
  path: "/about/testimonials",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Testimonials", path: PAGE.path },
];

export function meta() {
  return buildMeta({ ...PAGE, jsonLd: [breadcrumbJsonLd(CRUMBS)] });
}

const isDot = (t) => /Department of Transportation/.test(t.org);

export async function loader() {
  const all = readJson("testimonials");
  // The one quote the page leads with; the rest are grouped by who is speaking.
  const spotlight = all.find((t) => t.spotlight) || all[0];
  const rest = all.filter((t) => t !== spotlight);
  // A quote that states a number leads its group, as the number.
  const statFirst = (list) => [...list].sort((a, b) => (b.stat ? 1 : 0) - (a.stat ? 1 : 0));
  const count = (list) => new Set(list.map((t) => t.org)).size;
  return {
    spotlight,
    dots: statFirst(rest.filter(isDot)),
    firms: statFirst(rest.filter((t) => !isDot(t))),
    figures: {
      people: all.length,
      dots: count(all.filter(isDot)),
      firms: count(all.filter((t) => !isDot(t))),
    },
  };
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["firms"];
const surface = surfaceFor(LIGHT_SECTIONS);

/** Quotes in balanced columns, each card as tall as its quote: no ragged rows, no gaps. */
function Wall({ items }) {
  return (
    <ul className={styles.wall}>
      {items.map((item) => (
        <li key={item.quote} className={styles.brick}>
          {item.stat ? <StatCard item={item} /> : <TestimonialCard item={item} />}
        </li>
      ))}
    </ul>
  );
}

export default function Testimonials({ loaderData }) {
  const { spotlight, dots, firms, figures } = loaderData;
  return (
    <>
      <section className={styles.hero} aria-labelledby="testimonials-title">
        <div className={`container ${styles.heroGrid}`}>
          <div className={styles.heroText}>
            <Breadcrumbs items={CRUMBS} />
            <h1 id="testimonials-title" className={styles.heroTitle}>
              What our clients are saying
            </h1>
            <p className={styles.heroLead}>
              DOT leaders, safety engineers and public involvement officers, and the engineers and
              contractors who delivered their projects, on Road Safety Audit 3D and 3D
              Visualization.
            </p>
            <ul className={styles.figures} aria-label="Who is speaking">
              <li>
                <strong>{figures.people}</strong> people
              </li>
              <li>
                <strong>{figures.dots}</strong> state DOTs
              </li>
              <li>
                <strong>{figures.firms}</strong> firms
              </li>
            </ul>
          </div>

          {/* The quote the page leads with, set as large as the headline beside it. */}
          <figure className={styles.pull}>
            <blockquote className={styles.pullQuote}>
              <p>&ldquo;{spotlight.quote}&rdquo;</p>
            </blockquote>
            <figcaption className={styles.pullWho}>
              {spotlight.photo ? (
                <Picture
                  src={spotlight.photo}
                  alt=""
                  ratio="1 / 1"
                  widths={[160, 320]}
                  fallbackWidth={320}
                  sizes="88px"
                  className={styles.pullPhoto}
                />
              ) : null}
              <span>
                <strong className={styles.pullName}>{spotlight.author}</strong>
                {spotlight.role ? <span className={styles.pullRole}>{spotlight.role}</span> : null}
                <span className={styles.pullRole}>{spotlight.org}</span>
              </span>
            </figcaption>
            {spotlight.link || spotlight.video ? (
              <p className={styles.pullLinks}>
                {spotlight.link ? <Link to={spotlight.link.to}>{spotlight.link.label}</Link> : null}
                {spotlight.video ? (
                  <a href={spotlight.video.href} target="_blank" rel="noreferrer">
                    {spotlight.video.label}
                  </a>
                ) : null}
              </p>
            ) : null}
          </figure>
        </div>
      </section>

      <section className="section" aria-labelledby="dots-title">
        <div className="container">
          <SectionHeader
            id="dots-title"
            title="From the state DOTs"
            aside={`Leadership, safety engineering and public involvement, at ${figures.dots} state DOTs.`}
            link={{ to: "/case-studies", label: "Read the case studies" }}
          />
          <Wall items={dots} />
        </div>
      </section>

      <section className="section" data-surface={surface("firms")} aria-labelledby="firms-title">
        <div className="container">
          <SectionHeader
            id="firms-title"
            title="From the firms that deliver the projects"
            aside="Engineering firms and a contractor who used the models on DOT projects."
            link={{ to: "/projects", label: "See the projects" }}
          />
          <ul className={styles.rows}>
            {firms.map((item) => (
              <li key={item.quote}>
                <TestimonialRow item={item} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/case-studies/henderson-interchange/hero"
        title="See why they said it"
        text="Start with one corridor or one design, and judge it on your own project."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
